'use client';

// This import must go first
import 'src/http-setup';
import { useEffect } from 'react';
import { designSystemConfig } from '@mentimeter/ragnar-dsc';
import { themes } from '@mentimeter/ragnar-colors';
import { DeviceProvider } from '@mentimeter/ragnar-device';
import { DynamicIntercomLoader } from '@mentimeter/content-ui/providers/DynamicIntercomLoader';
import { DevelopmentToolsSetup } from '@mentimeter/content-ui/providers/DevelopmentToolsSetup';
import { ThemeProvider } from '@mentimeter/ragnar-react';
import { FelaProvider } from '@mentimeter/content-ui/providers/FelaProvider';
import { stripAllNonNumericChars } from '@mentimeter/ragnar-utils';
import { LocalStorage } from '@mentimeter/storage';
import Cookies from '@mentimeter/cookies';
import { USER_LOCALE_STORAGE_KEY } from '@mentimeter/i18n/constants';
import { ConfidenceProvider } from 'src/app/[lang]/(uncategorized)/ConfidenceProvider';
import {
  getCountryFromLocalStorage,
  getPreferredLanguageFromLocalStorage,
  setVisitorDataCache,
} from '@mentimeter/visitor/visitor-data';
import { AppStateProvider } from './AppStateProvider';

const DSC = {
  ...designSystemConfig,
  ...themes.light,
  //           0   1   2   3   4   5   6   7   8   9
  fontSizes: [12, 14, 16, 20, 24, 32, 36, 38, 48, 64],
  lineHeights: {
    none: 1,
    body: 1.4,
    heading: 1,
  },
};

const breakpoints = DSC.breakpoints.map<number>(stripAllNonNumericChars);

const CONFIDENCE_CLIENT_TOKEN = process.env.NEXT_PUBLIC_CONFIDENCE_CLIENT_TOKEN;

export function Providers({
  lang,
  newlyGeneratedServerToken,
  country,
  userAgent,
  children,
}: {
  lang: string;
  newlyGeneratedServerToken: string;
  country: string | null;
  userAgent: string | null;
  children: React.ReactNode;
}) {
  useMigrateLocalStorageLocaleToCookie();

  if (getPreferredLanguageFromLocalStorage() === null) {
    setVisitorDataCache({
      preferredLanguage: lang,
    });
  }

  if (getCountryFromLocalStorage() === null) {
    setVisitorDataCache({
      country: country ?? 'none',
    });
  }

  return (
    <ThemeProvider theme={DSC}>
      <DeviceProvider breakpoints={breakpoints}>
        <FelaProvider>
          <AppStateProvider
            newlyGeneratedServerToken={newlyGeneratedServerToken}
          >
            <ConfidenceProvider
              userAgent={userAgent ?? ''}
              clientToken={CONFIDENCE_CLIENT_TOKEN}
            >
              <DynamicIntercomLoader />
              <DevelopmentToolsSetup>{children}</DevelopmentToolsSetup>
            </ConfidenceProvider>
          </AppStateProvider>
        </FelaProvider>
      </DeviceProvider>
    </ThemeProvider>
  );
}

// When we removed the use of locale in LocalStorage this is not needed.
function useMigrateLocalStorageLocaleToCookie() {
  useEffect(() => {
    const localStorageLocale = LocalStorage.getItem(USER_LOCALE_STORAGE_KEY);
    const cookieLocale = Cookies.get(USER_LOCALE_STORAGE_KEY);
    if (localStorageLocale && !cookieLocale) {
      Cookies.set({
        type: 'functional',
        name: USER_LOCALE_STORAGE_KEY,
        value: localStorageLocale,
        options: {
          expires: 365,
          secure: true,
          sameSite: 'lax',
        },
      });
      window.location.reload();
    }
  }, []);
}
